import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { keyframes } from "@emotion/react";

const COLORS = ["#8FBDC4", "#B6C88F", "#E0B36A", "#A6B8D9", "#B7A9C9"];
const GRID_SIZE = 5;

// Helper to generate a random board with a guaranteed floodable group in top-left
function getInitialGrid() {
  return [
    [0, 2, 1, 2, 3],
    [4, 1, 2, 3, 4],
    [1, 2, 3, 4, 0],
    [2, 3, 4, 0, 1],
    [3, 4, 0, 1, 2],
  ];
}

// Keyframes for win jump animation
const winJump = keyframes`
  0% { transform: scale(1); }
  20% { transform: scale(1.25); }
  40% { transform: scale(0.9); }
  60% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

export default function MiniGridAnimation({ step }) {
  const [grid, setGrid] = useState(getInitialGrid());
  const [animStep, setAnimStep] = useState(0);
  const [loopKey, setLoopKey] = useState(0); // for forcing re-run
  const [winAnim, setWinAnim] = useState(false);

  // Animation logic for each onboarding step
  useEffect(() => {
    setGrid(getInitialGrid());
    setAnimStep(0);
    setWinAnim(false);
    let timeouts = [];

    function startAnimation() {
      if (step === 0) {
        // Step 1: Tap color, flood top-left group
        timeouts.push(
          setTimeout(() => {
            setGrid((g) =>
              g.map((row, r) =>
                row.map((c, col) =>
                  (r === 0 && col <= 1) || (r === 1 && col === 0) ? 2 : c
                )
              )
            );
            setAnimStep(1);
          }, 700)
        );
        timeouts.push(
          setTimeout(() => {
            setGrid(getInitialGrid());
            setAnimStep(0);
          }, 1700)
        );
      } else if (step === 1) {
        // Step 2: Animate spreading from top-left
        timeouts.push(
          setTimeout(() => {
            setGrid((g) =>
              g.map((row, r) =>
                row.map((c, col) => (r <= 1 && col <= 1 ? 1 : c))
              )
            );
            setAnimStep(1);
            timeouts.push(
              setTimeout(() => {
                setGrid((g) =>
                  g.map((row, r) =>
                    row.map((c, col) =>
                      (r === 0 && col === 3) ||
                      (r === 1 && col === 2) ||
                      (r === 2 && col === 1) ||
                      (r === 3 && col === 0)
                        ? 1
                        : c
                    )
                  )
                );
                setAnimStep(2);
              }, 600)
            );
          }, 600)
        );
        timeouts.push(
          setTimeout(() => {
            setGrid(getInitialGrid());
            setAnimStep(0);
          }, 2000)
        );
      } else if (step === 2) {
        // Step 3: Animate board filling to one color in several steps
        // Step 1: Fill top-left tile
        timeouts.push(
          setTimeout(() => {
            setGrid((g) =>
              g.map((row, r) =>
                row.map((c, col) => (r === 0 && col === 0 ? 3 : c))
              )
            );
            setAnimStep(1);
          }, 350)
        );
        // Step 2: Fill (0,1), (1,0)
        timeouts.push(
          setTimeout(() => {
            setGrid((g) =>
              g.map((row, r) =>
                row.map((c, col) =>
                  (r === 0 && (col === 0 || col === 1)) ||
                  (r === 1 && col === 0)
                    ? 3
                    : c
                )
              )
            );
            setAnimStep(2);
          }, 700)
        );
        // Step 3: Fill (0,2), (1,1), (2,0)
        timeouts.push(
          setTimeout(() => {
            setGrid((g) =>
              g.map((row, r) =>
                row.map((c, col) =>
                  (r === 0 && col <= 2) ||
                  (r === 1 && col <= 1) ||
                  (r === 2 && col === 0)
                    ? 3
                    : c
                )
              )
            );
            setAnimStep(3);
          }, 1050)
        );
        // Step 4: Fill (0,3), (1,2), (2,1), (3,0)
        timeouts.push(
          setTimeout(() => {
            setGrid((g) =>
              g.map((row, r) =>
                row.map((c, col) =>
                  (r === 0 && col <= 3) ||
                  (r === 1 && col <= 2) ||
                  (r === 2 && col <= 1) ||
                  (r === 3 && col === 0)
                    ? 3
                    : c
                )
              )
            );
            setAnimStep(4);
          }, 1400)
        );
        // Step 5: Fill (0,4), (1,3), (2,2), (3,1), (4,0)
        timeouts.push(
          setTimeout(() => {
            setGrid((g) =>
              g.map((row, r) =>
                row.map((c, col) =>
                  (r === 0 && col <= 4) ||
                  (r === 1 && col <= 3) ||
                  (r === 2 && col <= 2) ||
                  (r === 3 && col <= 1) ||
                  (r === 4 && col === 0)
                    ? 3
                    : c
                )
              )
            );
            setAnimStep(5);
          }, 1750)
        );
        // Step 6: Fill (1,4), (2,3), (3,2), (4,1)
        timeouts.push(
          setTimeout(() => {
            setGrid((g) =>
              g.map((row, r) =>
                row.map((c, col) =>
                  (r === 0 && col <= 4) ||
                  (r === 1 && col <= 4) ||
                  (r === 2 && col <= 3) ||
                  (r === 3 && col <= 2) ||
                  (r === 4 && col <= 1)
                    ? 3
                    : c
                )
              )
            );
            setAnimStep(6);
          }, 2100)
        );
        // Step 7: Fill (2,4), (3,3), (4,2)
        timeouts.push(
          setTimeout(() => {
            setGrid((g) =>
              g.map((row, r) =>
                row.map((c, col) =>
                  (r === 0 && col <= 4) ||
                  (r === 1 && col <= 4) ||
                  (r === 2 && col <= 4) ||
                  (r === 3 && col <= 3) ||
                  (r === 4 && col <= 2)
                    ? 3
                    : c
                )
              )
            );
            setAnimStep(7);
          }, 2450)
        );
        // Step 8: Fill (3,4), (4,3)
        timeouts.push(
          setTimeout(() => {
            setGrid((g) =>
              g.map((row, r) =>
                row.map((c, col) =>
                  (r === 0 && col <= 4) ||
                  (r === 1 && col <= 4) ||
                  (r === 2 && col <= 4) ||
                  (r === 3 && col <= 4) ||
                  (r === 4 && col <= 3)
                    ? 3
                    : c
                )
              )
            );
            setAnimStep(8);
          }, 2800)
        );
        // Step 9: Fill (4,4) (all filled)
        timeouts.push(
          setTimeout(() => {
            setGrid(
              Array(GRID_SIZE)
                .fill()
                .map(() => Array(GRID_SIZE).fill(3))
            );
            setAnimStep(9);
            setWinAnim(true);
          }, 3150)
        );
        // Win animation pause
        timeouts.push(
          setTimeout(() => {
            setWinAnim(false);
            setGrid(getInitialGrid());
            setAnimStep(0);
          }, 4650)
        );
      }
    }

    startAnimation();
    // Loop the animation
    const loop = setInterval(
      () => {
        setLoopKey((k) => k + 1);
      },
      step === 2 ? 4650 : step === 1 ? 2000 : 1700
    );

    return () => {
      timeouts.forEach(clearTimeout);
      clearInterval(loop);
    };
    // eslint-disable-next-line
  }, [step, loopKey]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mb: 1,
        mt: 0.5,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${GRID_SIZE}, 1.1em)`,
          gridTemplateRows: `repeat(${GRID_SIZE}, 1.1em)`,
          gap: 0.25,
          justifyContent: "center",
          alignItems: "center",
        }}
        aria-label="Mini color flood animation"
      >
        {grid.map((row, r) =>
          row.map((colorIdx, c) => (
            <Box
              key={`${r}-${c}`}
              sx={{
                width: "1.1em",
                height: "1.1em",
                borderRadius: 0,
                background: COLORS[colorIdx],
                boxShadow:
                  step === 0 &&
                  ((animStep === 0 && r === 0 && c === 0) ||
                    (animStep === 1 &&
                      ((r === 0 && c === 0) ||
                        (r === 0 && c === 1) ||
                        (r === 1 && c === 0))))
                    ? "0 0 0 2px #222, 0 0 8px #E0B36A"
                    : undefined,
                animation:
                  step === 2 && winAnim
                    ? `${winJump} 0.7s cubic-bezier(0.34,1.56,0.64,1) both`
                    : undefined,
                animationDelay:
                  step === 2 && winAnim ? `${(r + c) * 0.07}s` : undefined,
                transition: "background 0.4s, box-shadow 0.3s",
              }}
            />
          ))
        )}
      </Box>
    </Box>
  );
}
